import { useCallback, useEffect, useRef } from "react";

import { trackVideoEvent } from "~/lib/track-events";

const COMPLETE_RATIO = 0.95;
// Matches the watch-feed sampler so completion is comparable between the two surfaces.
const SAMPLE_INTERVAL_MS = 100;

/**
 * Playback telemetry for a public page's video: the landing pages and the web cinema.
 *
 * Analytics only, and deliberately so: this hook issues no backend request at all. These pages are
 * open to visitors with no session, so anything that needs an authenticated call must stay out of
 * this path rather than be folded in behind a flag — that is `useVideoWatchTracking`'s job. The
 * event names match the ones the in-app surfaces emit, so all of them land in one table and stay
 * comparable.
 *
 * Per item (one episode), each event fires at most once, so a replay never double-counts:
 * - `video_enter` (ms_since_enter = 0) — the zero point, reported by the caller via `notifyEnter`.
 * - `video_first_frame` — startup latency, preferring the frame actually painted (`rvfc`).
 * - `video_start` — first sample with real playback progress.
 * - `video_complete` — first sample at/after 95% of duration.
 *
 * Stalls are the exception and fire repeatedly, because their count per play IS the metric:
 * - `video_stall` on `waiting`, `video_stall_end` on the following `playing`. Both carry
 *   `ms_since_enter`, so a stall's duration is the difference between the pair, and a stall with no
 *   matching end is a visitor who left while buffering — the case a duration-only event would lose.
 *   Only counted once playback has actually advanced: `waiting` also fires during initial
 *   buffering, which is startup latency (already measured by `video_first_frame`), not an
 *   interruption.
 */
export function useVideoPlaybackTracking(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  itemId: string | undefined,
  videoUrl: string,
  surface: string
) {
  const enterTimeRef = useRef<number | null>(null);
  const firstFrameSentRef = useRef(false);
  const startSentRef = useRef(false);
  const completeSentRef = useRef(false);
  const stalledRef = useRef(false);
  const cancelFirstFrameRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    enterTimeRef.current = null;
    firstFrameSentRef.current = false;
    startSentRef.current = false;
    completeSentRef.current = false;
    stalledRef.current = false;

    return () => {
      cancelFirstFrameRef.current?.();
      cancelFirstFrameRef.current = null;
    };
  }, [itemId]);

  const msSinceEnter = useCallback(
    () => (enterTimeRef.current === null ? undefined : Math.round(performance.now() - enterTimeRef.current)),
    []
  );

  const notifyEnter = useCallback(() => {
    if (!itemId) return;
    if (enterTimeRef.current !== null) return;

    // Claim the enter slot only once the element exists, so the item never ends up with a
    // `video_enter` and no first-frame detection armed behind it.
    const video = videoRef.current;
    if (!video) return;

    enterTimeRef.current = performance.now();
    trackVideoEvent("video_enter", itemId, videoUrl, "entertainment", surface, 0);

    const emitFirstFrame = (source: "rvfc" | "loadeddata") => {
      if (firstFrameSentRef.current) return;
      firstFrameSentRef.current = true;
      cancelFirstFrameRef.current = null;
      trackVideoEvent("video_first_frame", itemId, videoUrl, "entertainment", surface, msSinceEnter(), source);
    };

    if (typeof video.requestVideoFrameCallback === "function") {
      const handle = video.requestVideoFrameCallback(() => emitFirstFrame("rvfc"));
      cancelFirstFrameRef.current = () => video.cancelVideoFrameCallback(handle);
    } else if (video.readyState >= video.HAVE_CURRENT_DATA) {
      emitFirstFrame("loadeddata");
    } else {
      const handleLoadedData = () => emitFirstFrame("loadeddata");
      video.addEventListener("loadeddata", handleLoadedData, { once: true });
      cancelFirstFrameRef.current = () => video.removeEventListener("loadeddata", handleLoadedData);
    }
  }, [videoRef, itemId, videoUrl, surface, msSinceEnter]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !itemId) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const sample = () => {
      const current = video.currentTime;

      if (!startSentRef.current && current > 0) {
        startSentRef.current = true;
        trackVideoEvent("video_start", itemId, videoUrl, "entertainment", surface, msSinceEnter());
      }

      if (!completeSentRef.current) {
        const duration = video.duration;
        if (Number.isFinite(duration) && duration > 0 && current / duration >= COMPLETE_RATIO) {
          completeSentRef.current = true;
          trackVideoEvent("video_complete", itemId, videoUrl, "entertainment", surface, msSinceEnter());
        }
      }
    };

    const startSampling = () => {
      if (timer === null) timer = setInterval(sample, SAMPLE_INTERVAL_MS);
    };
    const stopSampling = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const handleWaiting = () => {
      // Read the clock rather than wait for the sampler to have noticed it. Gating on the sampler
      // threw away any stall inside the first sampling interval, and on a cold connection that is
      // the likeliest stall there is. currentTime > 0 is the same fact, one tick earlier.
      if (video.currentTime === 0 || stalledRef.current) return;
      stalledRef.current = true;
      trackVideoEvent("video_stall", itemId, videoUrl, "entertainment", surface, msSinceEnter());
    };

    const handlePlaying = () => {
      startSampling();
      if (!stalledRef.current) return;
      stalledRef.current = false;
      trackVideoEvent("video_stall_end", itemId, videoUrl, "entertainment", surface, msSinceEnter());
    };

    video.addEventListener("play", startSampling);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("pause", stopSampling);
    video.addEventListener("ended", stopSampling);

    // Autoplay may already be running by the time these listeners attach.
    if (!video.paused) startSampling();

    return () => {
      video.removeEventListener("play", startSampling);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("pause", stopSampling);
      video.removeEventListener("ended", stopSampling);
      stopSampling();
    };
  }, [videoRef, itemId, videoUrl, surface, msSinceEnter]);

  return { notifyEnter };
}
